import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import SmoothScroll from "@/lib/SmoothScroll";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import CursorDot from "@/components/site/CursorDot";
import Loader from "@/components/site/Loader";
import MobileSticky from "@/components/site/MobileSticky";
import { Toaster } from "sonner";
import { useEffect } from "react";

import HomePage from "@/pages/HomePage";
import DogTraining from "@/pages/DogTraining";
import WorkWithToriano from "@/pages/WorkWithToriano";
import About from "@/pages/About";
import BooksMedia from "@/pages/BooksMedia";
import GoldenWolf from "@/pages/GoldenWolf";
import ContactPage from "@/pages/ContactPage";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Live from "@/pages/Live";
import LiveAuction from "@/pages/LiveAuction";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLiveScreen from "@/pages/admin/AdminLiveScreen";
import NyeEvent from "@/pages/NyeEvent";
import NyeSuccess from "@/pages/NyeSuccess";

function Chrome({ children }) {
  const loc = useLocation();
  const bare = loc.pathname === "/live" || loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/nye");
  return (
    <>
      {!bare && <Nav />}
      {children}
      {!bare && <Footer />}
      {!bare && <MobileSticky />}
    </>
  );
}

function App() {
  useEffect(() => {
    document.title = "Toriano Sanzone — Master Dog Trainer, Founder, Strategist";
    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    setMeta("description", "Toriano Sanzone — Master Dog Trainer, founder of Wolfkeeper University, author, speaker and AI strategist. Chicago's most trusted dog training. Consulting, speaking, and the Golden Wolf Foundation.");
    setMeta("keywords", "Master Dog Trainer Chicago, Chicago Dog Training, Dog Boot Camp Chicago, Behavioral Dog Training Chicago, Service Dog Training Chicago, Puppy Training Chicago, Private Dog Trainer Chicago, Dog Trainer Certification Chicago, Toriano Sanzone Dog Trainer, Wolfkeeper University, Toriano Sanzone, AI consultant Chicago, Business strategist Chicago, Golden Wolf Foundation, Veteran service-dog programs");
    setMeta("author", "Toriano Sanzone");

    // Open Graph
    const setProp = (prop, content) => {
      let m = document.querySelector(`meta[property="${prop}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute("property", prop); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    setProp("og:title", "Toriano Sanzone — Master Dog Trainer & Founder");
    setProp("og:description", "I train more than dogs. I build leaders. Chicago's most trusted dog training, business consulting, AI coaching, and the Golden Wolf Foundation.");
    setProp("og:type", "website");
    setProp("og:image", "https://customer-assets.emergentagent.com/job_golden-wolf-premium/artifacts/g0wrydcj_WhatsApp%20Image%202026-06-27%20at%2016.55.55.webp");

    // JSON-LD Person + LocalBusiness schema
    const existing = document.getElementById("ts-schema");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ts-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Person",
          "name": "Toriano Sanzone",
          "alternateName": "The Wolfkeeper",
          "jobTitle": "Master Dog Trainer, Founder, Author, Strategist",
          "telephone": "+1-312-933-1528",
          "url": "https://torianosanzone.com",
          "sameAs": ["https://torianosanzone.com"],
          "worksFor": { "@type": "Organization", "name": "Wolfkeeper University" }
        },
        {
          "@type": "LocalBusiness",
          "name": "Wolfkeeper University",
          "description": "Chicago's premier professional dog training — private, boot camp, service dog, behavioral rehab and Train-the-Trainer certification.",
          "telephone": "+1-312-933-1528",
          "address": { "@type": "PostalAddress", "addressLocality": "Chicago", "addressRegion": "IL", "addressCountry": "US" },
          "url": "https://torianosanzone.com/dog-training",
          "priceRange": "$$-$$$"
        },
        {
          "@type": "NonprofitOrganization",
          "name": "Golden Wolf Foundation",
          "description": "Veterans, service-dog training and workforce pathways.",
          "url": "https://torianosanzone.com/golden-wolf"
        }
      ]
    });
    document.head.appendChild(script);
  }, []);

  return (
    <BrowserRouter>
      <div className="App grain">
        <Loader />
        <CursorDot />
        <SmoothScroll>
          <Chrome>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/live" element={<Live />} />
              <Route path="/live/auction" element={<LiveAuction />} />
              <Route path="/nye" element={<NyeEvent />} />
              <Route path="/nye/success" element={<NyeSuccess />} />
              <Route path="/dog-training" element={<DogTraining />} />
              <Route path="/work-with-toriano" element={<WorkWithToriano />} />
              <Route path="/about" element={<About />} />
              <Route path="/books-media" element={<BooksMedia />} />
              <Route path="/golden-wolf" element={<GoldenWolf />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/live/:slug" element={<AdminLiveScreen />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="*" element={<HomePage />} />
            </Routes>
          </Chrome>
        </SmoothScroll>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#111110",
              color: "#f2ece1",
              border: "1px solid rgba(201,162,75,0.4)",
              fontFamily: "Manrope, sans-serif",
            },
          }}
        />
      </div>
    </BrowserRouter>
  );
}

export default App;
